// Offline Database Service using IndexedDB
// Handles local storage for offline-first functionality

const DB_NAME = 'kioo_crm_offline';
const DB_VERSION = 1;

class OfflineDatabase {
  constructor() {
    this.db = null;
    this.isReady = false;
    this.readyPromise = this.initDB();
  }

  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isReady = true;
        console.log('✅ Offline database initialized');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Sync Queue - stores pending operations
        if (!db.objectStoreNames.contains('syncQueue')) {
          const syncStore = db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
          syncStore.createIndex('type', 'type', { unique: false });
          syncStore.createIndex('status', 'status', { unique: false });
        }

        // Finance Income Cache
        if (!db.objectStoreNames.contains('incomeCache')) {
          const incomeStore = db.createObjectStore('incomeCache', { keyPath: 'id' });
          incomeStore.createIndex('date', 'date', { unique: false });
          incomeStore.createIndex('cachedAt', 'cachedAt', { unique: false });
        }

        // Finance Expenses Cache
        if (!db.objectStoreNames.contains('expenseCache')) {
          const expenseStore = db.createObjectStore('expenseCache', { keyPath: 'id' });
          expenseStore.createIndex('date', 'date', { unique: false });
          expenseStore.createIndex('cachedAt', 'cachedAt', { unique: false });
        }

        // Contacts Cache
        if (!db.objectStoreNames.contains('contactsCache')) {
          const contactsStore = db.createObjectStore('contactsCache', { keyPath: 'id' });
          contactsStore.createIndex('name', 'name', { unique: false });
          contactsStore.createIndex('cachedAt', 'cachedAt', { unique: false });
        }

        // Receipts Cache (metadata only, images stored as blobs)
        if (!db.objectStoreNames.contains('receiptsCache')) {
          const receiptsStore = db.createObjectStore('receiptsCache', { keyPath: 'id' });
          receiptsStore.createIndex('status', 'status', { unique: false });
          receiptsStore.createIndex('cachedAt', 'cachedAt', { unique: false });
        }

        // Receipt Images (blob storage)
        if (!db.objectStoreNames.contains('receiptImages')) {
          db.createObjectStore('receiptImages', { keyPath: 'id' });
        }

        // Dashboard Cache
        if (!db.objectStoreNames.contains('dashboardCache')) {
          db.createObjectStore('dashboardCache', { keyPath: 'key' });
        }

        // Settings Cache
        if (!db.objectStoreNames.contains('settingsCache')) {
          db.createObjectStore('settingsCache', { keyPath: 'key' });
        }

        // Last Sync Timestamps
        if (!db.objectStoreNames.contains('syncMeta')) {
          db.createObjectStore('syncMeta', { keyPath: 'key' });
        }

        console.log('📦 IndexedDB schema created/upgraded');
      };
    });
  }

  async ensureReady() {
    if (!this.isReady) {
      await this.readyPromise;
    }
    return this.db;
  }

  // Generic CRUD operations
  async add(storeName, data) {
    await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.add({ ...data, cachedAt: Date.now() });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async put(storeName, data) {
    await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put({ ...data, cachedAt: Date.now() });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async get(storeName, key) {
    await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAll(storeName) {
    await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async delete(storeName, key) {
    await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async clear(storeName) {
    await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async bulkPut(storeName, items) {
    await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const timestamp = Date.now();
      
      items.forEach(item => {
        store.put({ ...item, cachedAt: timestamp });
      });

      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Sync Queue Operations
  async addToSyncQueue(operation) {
    const queueItem = {
      ...operation,
      timestamp: Date.now(),
      status: 'pending',
      retryCount: 0,
      maxRetries: 3
    };
    return this.add('syncQueue', queueItem);
  }

  async getPendingSyncItems() {
    const items = await this.getAll('syncQueue');
    return items.filter(item => item.status === 'pending' || item.status === 'retry');
  }

  async updateSyncItemStatus(id, status, error = null) {
    const item = await this.get('syncQueue', id);
    if (item) {
      item.status = status;
      item.lastAttempt = Date.now();
      if (error) item.lastError = error;
      if (status === 'retry') item.retryCount = (item.retryCount || 0) + 1;
      await this.put('syncQueue', item);
    }
  }

  async removeSyncItem(id) {
    return this.delete('syncQueue', id);
  }

  // Cache Management
  async cleanOldCache(maxAgeDays = 30) {
    const maxAge = maxAgeDays * 24 * 60 * 60 * 1000;
    const cutoff = Date.now() - maxAge;
    
    const stores = ['incomeCache', 'expenseCache', 'contactsCache', 'receiptsCache'];
    
    for (const storeName of stores) {
      const items = await this.getAll(storeName);
      for (const item of items) {
        if (item.cachedAt && item.cachedAt < cutoff) {
          await this.delete(storeName, item.id);
        }
      }
    }
    
    console.log('🧹 Old cache cleaned');
  }

  // Get sync metadata
  async getLastSyncTime(key) {
    const meta = await this.get('syncMeta', key);
    return meta?.timestamp || null;
  }

  async setLastSyncTime(key) {
    await this.put('syncMeta', { key, timestamp: Date.now() });
  }
}

// Create singleton instance
const offlineDB = new OfflineDatabase();

export default offlineDB;
